import "./list-registration.scss";

import { RegistrationStatus } from "@core/model/registration-status.enum";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import TagColor from "@core/ui/tag-color/tag-color";
import { useCardCategory } from "@domain/person/card-category.hook";
import { GetPersonModel } from "@factory/model/query-model-person";
import { GetRegistrationModel } from "@factory/model/query-model-registration";
import { useQyAutoAssignCardSeries } from "@factory/query/card-series.query";
import {
  useQyGetRegistration,
  useQyProceedRegistration,
} from "@factory/query/registration.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import useScreenSize from "@shared/utility/screen-size";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { FilterRegistration } from "../filter-registration/filter-registration";
import { useFilterRegistrationContext } from "../filter-registration/filter-registration.context";

export function ListRegistration() {
  // THIS IS THE LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { tenant } = getUserRole();
  const { cardCategoryDropdown, selectedCard } = useCardCategory();

  const { isMobileMode } = useScreenSize();
  const { formatDate } = useDateTimeContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterRegistrationContext();
  const [approveDialog, setApproveDialog] = useState({
    show: false,
    data: null as GetRegistrationModel | null,
  });
  const { showError, showSuccess } = useNotificationContext();

  // THIS IS THE QUERY DECLARATION
  const {
    data: registrationResponse,
    isLoading,
    isError,
    error,
  } = useQyGetRegistration(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues()
  );

  const { mutateAsync: proceedRegistration } = useQyProceedRegistration();
  const { mutateAsync: assignCard } = useQyAutoAssignCardSeries();

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };
  const handleRegister = async () => {
    const customerData = approveDialog.data;
    const userCode = customerData?.code;
    if (!userCode || !selectedCard) {
      showError("No usercode or card category is not selected");
      return;
    }

    try {
      const registrationResponse = await proceedRegistration({
        code: userCode,
        branch: customerData?.branch,
      });
      const registrationResponseData = registrationResponse.data;

      await assignCard({
        user_code: (registrationResponseData as GetPersonModel)?.code ?? "",
        card_category: selectedCard,
        tenant,
      });

      setApproveDialog({ data: null, show: false });
      showSuccess(
        "Congratulations! You have successfully registered a new customer"
      );
    } catch (error) {
      console.error(error);
    }
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterRegistration />
      </Sidebar>
    </section>
  );
  const footerContent = (
    <div>
      <Button
        className="block w-full mt-2"
        label="Register"
        onClick={() => handleRegister()}
      />
      <Button
        className="block w-full mt-2"
        label="Close"
        severity="secondary"
        outlined
        onClick={() => setApproveDialog((item) => ({ ...item, show: false }))}
      />
    </div>
  );
  const approveElement = (
    <Dialog
      footer={footerContent}
      header={
        approveDialog?.data
          ? `${approveDialog.data.first_name} ${approveDialog.data.last_name}`
          : "Approve Confirmation"
      }
      visible={approveDialog.show}
      style={{ width: "50vw" }}
      onHide={() => {
        if (!approveDialog) return;

        setApproveDialog((item) => ({ ...item, show: false }));
      }}
      maximized={isMobileMode}
    >
      <p>Select a card category to proceed</p>
      {cardCategoryDropdown}
    </Dialog>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{registrationResponse?.count}</b>
      </h5>

      <DataTable
        value={registrationResponse?.data}
        selectionMode="single"
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="first_name" header="First Name" sortable></Column>
        <Column field="middle_name" header="Middle Name" sortable></Column>
        <Column field="last_name" header="Last Name" sortable></Column>
        <Column field="gender" header="Gender" sortable></Column>
        <Column
          field="birthday"
          header="Birthday"
          body={(cell) => formatDate(cell.birthday)}
          sortable
        ></Column>
        <Column field="username" header="Username" sortable></Column>
        <Column field="email" header="Email" sortable></Column>
        <Column field="mobile_number" header="Mobile Number" sortable></Column>
        <Column
          field="status"
          header="Status"
          body={(cell) => <TagColor value={cell.status} />}
          sortable
        ></Column>
        <Column
          header="Action"
          body={(cell) => (
            <>
              {cell.status === RegistrationStatus.PENDING ? (
                <div className="flex flex-col gap-2">
                  <Button
                    label="Approve"
                    size="small"
                    outlined
                    onClick={() => setApproveDialog({ data: cell, show: true })}
                  />
                </div>
              ) : null}
            </>
          )}
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={registrationResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListRegistration" className="registration">
      <HeaderContent title={<h1>Registration</h1>}></HeaderContent>
      {approveElement}

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListRegistration;
